import React from 'react';
import { Helmet } from 'react-helmet-async';

const MAPBOX_SCRIPT_ID = 'mapbox_GL_JS';
const GOOGLE_MAPS_SCRIPT_ID = 'GoogleMapsApi';

// 🔥 Hotjar config
const HOTJAR_ID = 6510344;
const HOTJAR_VERSION = 6;

// ✅ Environment check
const IS_PRODUCTION = process.env.REACT_APP_ENV === 'production';

export const IncludeScripts = props => {
  const { marketplaceRootURL: rootURL, maps, googleAnalyticsId } = props?.config || {};
  const { mapProvider, googleMapsAPIKey, mapboxAccessToken } = maps || {};

  const isGoogleMapsInUse = mapProvider === 'GOOGLE_MAPS';
  const isMapboxInUse = mapProvider === 'MAPBOX';
  const hasGoogleAnalyticsv4Id = googleAnalyticsId?.startsWith('G-');

  let mapLibraries = [];
  let analyticsLibraries = [];

  /* ---------------- MAP LIBRARIES ---------------- */
  if (isMapboxInUse) {
    mapLibraries.push(
      <script
        key="mapboxSDK"
        src={`${rootURL}/static/scripts/mapbox/mapbox-sdk.min.js`}
      />
    );

    mapLibraries.push(
      <link
        key="mapbox_GL_CSS"
        href="https://api.mapbox.com/mapbox-gl-js/v1.0.0/mapbox-gl.css"
        rel="stylesheet"
        crossOrigin
      />
    );

    mapLibraries.push(
      <script
        id={MAPBOX_SCRIPT_ID}
        key="mapbox_GL_JS"
        src="https://api.mapbox.com/mapbox-gl-js/v1.0.0/mapbox-gl.js"
        crossOrigin
      />
    );
  }

  if (isGoogleMapsInUse) {
    mapLibraries.push(
      <script
        id={GOOGLE_MAPS_SCRIPT_ID}
        key="GoogleMapsApi"
        src={`https://maps.googleapis.com/maps/api/js?key=${googleMapsAPIKey}&libraries=places`}
        crossOrigin
      />
    );
  }

  /* ---------------- GOOGLE ANALYTICS ---------------- */
  if (googleAnalyticsId && hasGoogleAnalyticsv4Id) {
    analyticsLibraries.push(
      <script
        key="gtag-js"
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
        crossOrigin
      />
    );

    analyticsLibraries.push(
      <script key="gtag-inline">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${googleAnalyticsId}', {
            cookie_flags: 'SameSite=None;Secure',
          });
        `}
      </script>
    );
  }

  /* ---------------- HOTJAR (PRODUCTION ONLY) ---------------- */
  if (IS_PRODUCTION) {
    analyticsLibraries.push(
      <script key="hotjar">
        {`
          (function(h,o,t,j,a,r){
            h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
            h._hjSettings={hjid:${HOTJAR_ID},hjsv:${HOTJAR_VERSION}};
            a=o.getElementsByTagName('head')[0];
            r=o.createElement('script');r.async=1;
            r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
            a.appendChild(r);
          })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
        `}
      </script>
    );
  }

  /* ---------------- MAPBOX TOKEN HANDLING ---------------- */
  const isBrowser = typeof window !== 'undefined';
  const isMapboxLoaded = isBrowser && window.mapboxgl;

  if (isMapboxInUse && isMapboxLoaded && !window.mapboxgl.accessToken) {
    window.mapboxgl.accessToken = mapboxAccessToken;
  }

  const onMapLibLoaded = () => {
    if (isMapboxInUse && !window.mapboxgl.accessToken) {
      window.mapboxgl.accessToken = mapboxAccessToken;
    }
  };

  const onChangeClientState = (newState, addedTags) => {
    if (addedTags?.scriptTags) {
      const foundScript = addedTags.scriptTags.find(s =>
        [MAPBOX_SCRIPT_ID, GOOGLE_MAPS_SCRIPT_ID].includes(s.id)
      );
      if (foundScript) {
        foundScript.addEventListener('load', onMapLibLoaded, { once: true });
      }
    }
  };

  return (
    <Helmet onChangeClientState={onChangeClientState}>
      {[...analyticsLibraries, ...mapLibraries]}
    </Helmet>
  );
};
